/**
 * Widget Data Bridge — syncs app data to native widget storage.
 *
 * Android: writes to SharedPreferences via WidgetDataBridgeModule
 * iOS: writes to App Group UserDefaults via WidgetDataBridgeModule
 *
 * Called after every store mutation/sync to keep widgets up-to-date.
 */

import { Platform, NativeModules } from 'react-native';

const { WidgetDataBridge } = NativeModules;

interface WidgetReminder {
  seq: number;
  body: string;
  runAtUtc: string;
  status: 'active' | 'paused' | 'done' | 'overdue';
  recurrence: string | null;
  tags: string[];
  durationMinutes: number | null;
}

interface WidgetCalendarEvent {
  id: string;
  title: string;
  startUtc: string;
  endUtc: string;
  source: 'google' | 'reminder';
  color: string;
  isAllDay: boolean;
}

interface WidgetChatMessage {
  id: number;
  body: string;
  direction: 'inbound' | 'outbound';
  createdAt: string;
}

interface WidgetUserProfile {
  name: string;
  completionRate: number;
  totalActive: number;
  totalDone: number;
  language: string;
}

/** Check if native bridge is available (not on web) */
const isAvailable = Platform.OS !== 'web' && WidgetDataBridge != null;

/**
 * Sync reminder data to native widget storage.
 */
export async function syncRemindersToWidget(reminders: WidgetReminder[]): Promise<void> {
  if (!isAvailable) return;
  try {
    await WidgetDataBridge.setData('widget_reminders', JSON.stringify(reminders.slice(0, 20)));
  } catch (e) {
    console.warn('[WidgetBridge] syncReminders failed:', e);
  }
}

/**
 * Sync calendar events to native widget storage.
 */
export async function syncCalendarEventsToWidget(events: WidgetCalendarEvent[]): Promise<void> {
  if (!isAvailable) return;
  try {
    await WidgetDataBridge.setData('widget_calendar_events', JSON.stringify(events));
  } catch (e) {
    console.warn('[WidgetBridge] syncCalendarEvents failed:', e);
  }
}

/**
 * Sync chat messages to native widget storage.
 */
export async function syncChatMessagesToWidget(messages: WidgetChatMessage[]): Promise<void> {
  if (!isAvailable) return;
  try {
    await WidgetDataBridge.setData('widget_chat_messages', JSON.stringify(messages.slice(0, 10)));
  } catch (e) {
    console.warn('[WidgetBridge] syncChatMessages failed:', e);
  }
}

/**
 * Sync user profile data to native widget storage.
 */
export async function syncUserProfileToWidget(profile: WidgetUserProfile): Promise<void> {
  if (!isAvailable) return;
  try {
    await WidgetDataBridge.setData('widget_user_profile', JSON.stringify(profile));
  } catch (e) {
    console.warn('[WidgetBridge] syncUserProfile failed:', e);
  }
}

/**
 * Sync tag color overrides to native widget storage.
 */
export async function syncTagColorsToWidget(tagColors: Record<string, string> | null): Promise<void> {
  if (!isAvailable) return;
  try {
    await WidgetDataBridge.setData('widget_tag_colors', JSON.stringify(tagColors || {}));
  } catch (e) {
    console.warn('[WidgetBridge] syncTagColors failed:', e);
  }
}

/**
 * Trigger widget refresh on the native side.
 * Call after any data sync to update all widgets immediately.
 */
export async function refreshWidgets(): Promise<void> {
  if (!isAvailable) return;
  try {
    await WidgetDataBridge.refreshWidgets();
  } catch (e) {
    console.warn('[WidgetBridge] refreshWidgets failed:', e);
  }
}

/**
 * Full sync — call after syncFromServer in any store.
 * Pass all current data at once.
 */
export async function syncAllWidgetData(data: {
  reminders?: WidgetReminder[];
  calendarEvents?: WidgetCalendarEvent[];
  chatMessages?: WidgetChatMessage[];
  userProfile?: WidgetUserProfile;
}): Promise<void> {
  if (!isAvailable) return;
  const promises: Promise<void>[] = [];
  if (data.reminders) promises.push(syncRemindersToWidget(data.reminders));
  if (data.calendarEvents) promises.push(syncCalendarEventsToWidget(data.calendarEvents));
  if (data.chatMessages) promises.push(syncChatMessagesToWidget(data.chatMessages));
  if (data.userProfile) promises.push(syncUserProfileToWidget(data.userProfile));
  await Promise.all(promises);
  await refreshWidgets();
}
